import type {
    ContactStep,
    NavLink,
    ProcessStep,
    Service,
    Stat,
    Testimonial,
    WorkItem
} from '@/types'

export const NAV_LINKS: Array<NavLink> = [
    { label: 'Work', href: '/#work' },
    { label: 'Services', href: '/#services' },
    { label: 'Process', href: '/#process' },
    { label: 'Studio', href: '/#studio' }
]

export const FOOTER_LINKS: Array<NavLink> = [
    ...NAV_LINKS,
    { label: 'Start a project', href: '/#contact' }
]

export const HERO_PROOF_IMAGES: Array<string> = [
    '/images/work/scorch-society.png',
    '/images/work/moonmilk.png',
    '/images/work/dew-club.png',
    '/images/work/bean-riot.png'
]

export const MARQUEE_SERVICES: Array<string> = [
    'Brand strategy',
    'Naming',
    'Visual identity',
    'Packaging',
    '3D product renders',
    'Launch campaigns'
]

export const MARQUEE_CLIENTS: Array<string> = [
    'Fizz Theory',
    'Scorch Society',
    'Moonmilk',
    'Grain Gang',
    'Dew Club',
    'Bean Riot',
    'Sunday Scoop',
    'Pip & Pickle',
    'Tidal Tea'
]

export const STUDIO_STATS: Array<Stat> = [
    { value: '160+', label: 'Consumer brands launched' },
    { value: '12', label: 'Years of making noise' },
    { value: '41', label: 'Design and packaging awards' },
    { value: '3.4×', label: 'Average first-year sales lift' }
]

export const WORK_ITEMS: Array<WorkItem> = [
    {
        slug: 'fizz-theory',
        client: 'Fizz Theory',
        category: 'Prebiotic soda',
        year: '2026',
        summary: 'A gut-friendly soda that tastes like candy. We built a firework burst system that turns every can into a tiny celebration in the cold case.',
        result: { value: '+212%', label: 'Retail velocity in six months' },
        deliverables: ['Identity', 'Packaging', '3D renders'],
        image: '/images/work/fizz-theory.png',
        imageAlt: 'Three Fizz Theory soda cans in cobalt, lime and orange flying through ice cubes and bubbles on a pink background',
        tone: 'pink',
        layout: 'feature'
    },
    {
        slug: 'scorch-society',
        client: 'Scorch Society',
        category: 'Small-batch hot sauce',
        year: '2025',
        summary: 'Three heat levels, one loud flame. A label system that reads from across the aisle and gets hotter as the sauce does.',
        result: { value: '40k', label: 'Bottles sold out in three weeks' },
        deliverables: ['Naming', 'Identity', 'Packaging'],
        image: '/images/work/scorch-society.png',
        imageAlt: 'Three Scorch Society hot sauce bottles in red, orange and green on a cream podium, surrounded by chili peppers and paper flames',
        tone: 'yellow',
        layout: 'tall'
    },
    {
        slug: 'moonmilk',
        client: 'Moonmilk',
        category: 'Bedtime oat milk',
        year: '2025',
        summary: 'An oat milk made for the end of the day, wrapped in a night-sky pattern that turns the dairy aisle into a quiet little moment.',
        result: { value: '2,300', label: 'Stores stocked in year one' },
        deliverables: ['Strategy', 'Packaging', 'Launch'],
        image: '/images/work/moonmilk.png',
        imageAlt: 'Two Moonmilk oat milk cartons in cream and periwinkle covered in tiny moons and stars, with oat milk splashing around them',
        tone: 'lilac',
        layout: 'standard'
    },
    {
        slug: 'grain-gang',
        client: 'Grain Gang',
        category: 'Low-sugar cereal',
        year: '2024',
        summary: "Less sugar, more noise. Bold geometric color blocks that out-shout every cartoon mascot on the kids' shelf.",
        result: { value: '#1', label: 'New cereal in its category at launch' },
        deliverables: ['Identity', 'Packaging', '3D renders'],
        image: '/images/work/grain-gang.png',
        imageAlt: 'A Grain Gang cereal box in pink, yellow and blue blocks with colorful cereal rings bursting out of the top',
        tone: 'mint',
        layout: 'standard'
    },
    {
        slug: 'dew-club',
        client: 'Dew Club',
        category: 'Skincare',
        year: '2026',
        summary: 'Skincare that skips the beige. We designed the range and rendered it photoreal, so the brand could sell it before a single tube was filled.',
        result: { value: '9,000', label: 'Preorders taken on renders alone' },
        deliverables: ['Identity', 'Packaging', '3D renders'],
        image: '/images/work/dew-club.png',
        imageAlt: 'A Dew Club skincare tube, pump bottle and jar in lime, cobalt and pink on pastel pedestals with water droplets',
        tone: 'peach',
        layout: 'standard'
    },
    {
        slug: 'bean-riot',
        client: 'Bean Riot',
        category: 'Specialty coffee',
        year: '2024',
        summary: 'A coffee subscription that swapped kraft paper for neon. Every roast gets its own color and foil pattern, so the shelf looks like a party and reordering takes one glance.',
        result: { value: '+68%', label: 'Subscription growth after the rebrand' },
        deliverables: ['Identity', 'Packaging', 'Campaign'],
        image: '/images/work/bean-riot.png',
        imageAlt: 'Four Bean Riot coffee bags in neon orange, pink, blue and lime with foil stripes, with coffee beans flying around them',
        tone: 'violet',
        layout: 'wide'
    }
]

export const SERVICES: Array<Service> = [
    {
        title: 'Strategy',
        description: 'Positioning, naming and one sharp idea the whole brand can hang off. We find the thing only you can say, then help you say it louder.',
        deliverables: ['Positioning', 'Naming', 'Brand platform', 'Customer research'],
        tone: 'primary'
    },
    {
        title: 'Identity',
        description: 'Logos, type, color and a system bold enough for a billboard that still reads on a bottle cap.',
        deliverables: ['Logo and wordmark', 'Type and color', 'Guidelines', 'Motion identity'],
        tone: 'yellow'
    },
    {
        title: 'Packaging',
        description: 'Structural and graphic packaging built to win the three-second shelf test, then survive the supply chain.',
        deliverables: ['Structural design', 'Pack graphics', 'Range architecture', 'Print-ready artwork'],
        tone: 'pink'
    },
    {
        title: '3D renders',
        description: 'Photoreal CGI of your product before the factory makes a single unit, ready for pitches, preorders, ecommerce and ads.',
        deliverables: ['CGI stills', '360° spins', 'Launch films', 'AR-ready models'],
        tone: 'mint'
    },
    {
        title: 'Launch',
        description: 'Campaigns, social toolkits, retail displays and the stunts that get a new brand talked about on day one.',
        deliverables: ['Campaign ideas', 'Social toolkits', 'Retail displays', 'Launch events'],
        tone: 'lilac'
    }
]

export const PROCESS_STEPS: Array<ProcessStep> = [
    {
        number: '01',
        title: 'Dig',
        timing: 'Weeks 1–2',
        description: "We interview your customers, raid your competitors' shelves and find the one thing only you can say.",
        tone: 'yellow'
    },
    {
        number: '02',
        title: 'Define',
        timing: 'Weeks 3–4',
        description: 'Positioning, personality and a creative platform, signed off before a single logo gets drawn.',
        tone: 'pink'
    },
    {
        number: '03',
        title: 'Design',
        timing: 'Weeks 5–9',
        description: 'Identity, packaging and 3D renders, tested on real shelves and real phones, not just a mood board.',
        tone: 'mint'
    },
    {
        number: '04',
        title: 'Deliver',
        timing: 'Weeks 10–12',
        description: 'Print-ready artwork, guidelines and a launch kit, plus a team on call for the day you hit the shelf.',
        tone: 'blue'
    }
]

export const TESTIMONIALS: Array<Testimonial> = [
    {
        quote: "Wallop didn't just redesign our can. They made it the thing people post. Our velocity doubled before we changed a single ingredient.",
        name: 'Priya Nair',
        role: 'Co-founder',
        company: 'Fizz Theory',
        image: '/images/work/fizz-theory.png'
    },
    {
        quote: "They found the weird in us and turned it all the way up. Buyers call us the loud bottle now, and honestly, we'll take it.",
        name: 'Marcus Bell',
        role: 'Founder',
        company: 'Scorch Society',
        image: '/images/work/scorch-society.png'
    },
    {
        quote: "We opened preorders on renders alone. Not one customer guessed the products didn't exist yet.",
        name: 'Lena Ortiz',
        role: 'CEO',
        company: 'Dew Club',
        image: '/images/work/dew-club.png'
    },
    {
        quote: 'Kids now drag their parents down the aisle to our box. That was the entire brief, and they nailed it in one round.',
        name: 'Tom Okafor',
        role: 'Head of Marketing',
        company: 'Grain Gang',
        image: '/images/work/grain-gang.png'
    }
]

export const CONTACT_STEPS: Array<ContactStep> = [
    {
        title: 'We read it',
        description: "Every brief lands with a partner. You'll hear back within two working days."
    },
    {
        title: 'We talk',
        description: 'A 30-minute call to dig into the brand, the shelf and the deadline.'
    },
    {
        title: 'We quote',
        description: 'A fixed-price proposal in your inbox within a week. No retainer traps.'
    }
]

// Options offered by the project inquiry form. The API validates against the same lists.
export const SERVICE_OPTIONS: Array<string> = ['Strategy', 'Identity', 'Packaging', '3D renders', 'Launch']

export const BUDGET_OPTIONS: Array<string> = ['Under $25k', '$25k–$50k', '$50k–$100k', '$100k+']
